<template>
  <div class="settings-list">

    <Divider layout="vertical" />

    <IconTextBold
        v-tooltip.bottom="{ value: 'Bold', showDelay: 500, hideDelay: 100 }"
        @click="setBoldText"
        :class="isBold ? 'active' : ''"
        class="handler-item"
    />
    <IconTextItalic
        v-tooltip.bottom="{ value: 'Italic', showDelay: 500, hideDelay: 100 }"
        @click="setItalicText"
        :class="isItalic ? 'active' : ''"
        class="handler-item"
    />
    <IconTextUnderline
        v-tooltip.bottom="{ value: 'Underline', showDelay: 500, hideDelay: 100 }"
        @click="setUnderlineText"
        :class="isUnderlined ? 'active' : ''"
        class="handler-item"
    />

    <TextColorButton :color="colorHEX"
                     v-tooltip.bottom="{ value: 'Text Color', showDelay: 500, hideDelay: 100 }"
                     @click="$refs.colorPickerVisible.toggle($event);toggleColorPicker()" class="handler-item" >
      <IconText />
    </TextColorButton>
    <OverlayPanel ref="colorPickerVisible" class="preserve-text-select">
      <ColorPicker
          @color-selected="$refs.colorPickerVisible.toggle(false)"
          @edit-theme="$refs.colorPickerVisible.toggle(false);themeDialogVisible = true"
          v-model="colorHEX" inputId="cp-hex" format="hex"
      />
    </OverlayPanel>

    <TextColorButton :color="backgroundColorHEX"
                     v-tooltip.bottom="{ value: 'Background Color', showDelay: 500, hideDelay: 100 }"
                     @click="$refs.backgroundColorPickerVisible.toggle($event)" class="handler-item">
      <IconBackgroundColor />
    </TextColorButton>
    <OverlayPanel ref="backgroundColorPickerVisible">
      <ColorPicker
          @color-selected="$refs.backgroundColorPickerVisible.toggle(false)"
          @edit-theme="$refs.backgroundColorPickerVisible.toggle(false);themeDialogVisible = true"
          v-model="backgroundColorHEX" inputId="cp-hex" format="hex"
      />
    </OverlayPanel>

    <Divider layout="vertical" />

      <IconList v-tooltip.bottom="{ value: 'List', showDelay: 500, hideDelay: 100 }"
                   class="handler-item"
                :class="selectionList ? 'active' : ''"
                @click="setListState()"

      />
      <IconDown @click="$refs.listVisible.toggle($event)" style="margin-top: 8px" />

      <OverlayPanel class="linkOverlay" ref="listVisible">
        <IconDot v-tooltip.bottom="{ value: 'List', showDelay: 500, hideDelay: 100 }"
                  class="handler-item"
                  @click="setListState('disc')"
        />

        <span v-tooltip.bottom="{ value: 'List', showDelay: 500, hideDelay: 100 }"
                  class="handler-item"
                  @click="setListState('\'✔ \'')"
        >
          ✔
        </span>

        <span v-tooltip.bottom="{ value: 'List', showDelay: 500, hideDelay: 100 }"
              class="handler-item"
              @click="setListState('circle')"
        >
          ○
        </span>

        <span v-tooltip.bottom="{ value: 'List', showDelay: 500, hideDelay: 100 }"
              class="handler-item"
              @click="setListState('square')"
        >
          ■
        </span>

        <span v-tooltip.bottom="{ value: 'List', showDelay: 500, hideDelay: 100 }"
              class="handler-item"
              @click="setListState( 'decimal')"
        >
          123
        </span>

      </OverlayPanel>

    <div>
      <IconLinkOne v-tooltip.bottom="{ value: 'Insert Link', showDelay: 500, hideDelay: 100 }"
                class="handler-item"
                @click="$refs.linkVisible.toggle($event)"
      />
      <OverlayPanel class="linkOverlay" ref="linkVisible">
        <InputText type="text" placeholder="Add a Link (https://mylink.com)" style="font-size: 0.75rem; width: 250px;"  v-model="link" />
        <Button label="Add" style="font-size: 0.75rem;" @click="addLink"  />
        <p style="font-size: 12px;color:grey;padding-top:10px;">Note: The link will open in a new tab when clicked.</p>
      </OverlayPanel>
    </div>

    <Divider layout="vertical" />

    <div style="width: 125px; margin-top: 6px">
      <InputNumber class="font-incrementer" input-class="preserve-select-text" v-model="defaultFontSize" showButtons buttonLayout="horizontal"
                   style="height: 20px; width: 80px;  font-size: 12px;" :min="8" :max="200"
                   v-tooltip.bottom="{ value: 'Font size', showDelay: 500, hideDelay: 100 }"
                   @input="updateFontSize"
                   @keydown="handleFontSizeTyping($event)">
        <template #incrementbuttonicon>
          <IconPlus/>
        </template>
        <template #decrementbuttonicon>
          <IconMinus/>
        </template>
      </InputNumber>
    </div>
    <Divider layout="vertical" />
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch, watchEffect, nextTick} from 'vue'
import ColorPicker from "@/components/ColorPicker/index.vue";
import TextColorButton from "@/views/Editor/Toolbar/common/TextColorButton.vue";
import {storeToRefs} from "pinia";
import {useMainStore, useWidgetStore} from "@/store";
import emitter, { EmitterEvents } from '@/utils/emitter'

const widgetStore = useWidgetStore()
const { selectedWidget, selectionFontSize, textSelected, selectionBold, selectionItalic, selectionUnderline, selectionColor, selectionList } = storeToRefs(widgetStore)
const { selectedTableCells: selectedCells, themeDialogVisible } = storeToRefs(useMainStore())

const backgroundColorPickerVisible = ref()
const colorPickerVisible = ref()

const handleFontSizeTyping = (typingEvent) => {

  if (typingEvent.key === 'Enter' || typingEvent.key === 'Tab') {
    typingEvent.preventDefault()
    typingEvent.stopPropagation()
    textSelected.value && emitter.emit(EmitterEvents.RICH_TEXT_COMMAND, { action: { command: 'focus' } })
  }
}

// sync up local color picker with pinia so we can keep selections in prosemirror
const toggleColorPicker = () => {
  nextTick(() => {
    const colorPicker = document.getElementsByClassName('color-picker-input')[0]
    if (colorPicker) {
      colorPicker.focus()
    }
  })
}

const presetStyle = ref()
const linkVisible = ref()
const listVisible = ref()
const link = ref()
const isBold = computed(() => {
  const current = getCurrentTextStyle('fontWeight')
  if (selectedWidget.value && selectedWidget.value.type === 'table') {
    return 'bold' === current
  }
  return textSelected.value || (selectedWidget.value?.type === 'reg_text') ? selectionBold.value : 'bold' === current
})
const isItalic = computed(() => {
  const current = getCurrentTextStyle('fontStyle')
  if (selectedWidget.value && selectedWidget.value.type === 'table') {
    return 'italic' === current
  }
  return textSelected.value || (selectedWidget.value?.type === 'reg_text') ? selectionItalic.value : 'italic' === current
})
const isUnderlined = computed(() => {
  const current = getCurrentTextStyle('textDecoration')
  if (selectedWidget.value && selectedWidget.value.type === 'table') {
    return 'underline' === current
  }
  return textSelected.value || (selectedWidget.value?.type === 'reg_text') ? selectionUnderline.value : 'underline' === current
})

const setListState = (state: string | '') => {
  emitRichTextCommand('bulletList', state)
}

const presetStyles = [
  { value: 1, label: 'Headline', style: { fontSize: '30px', fontWeight: 700 } },
  { value: 2, label: 'Subtitle', style: { fontSize: '25px', fontWeight: 700 } },
  { value: 3, label: 'Normal Text', style: { fontSize: '16px', fontWeight: 400 } },
]

const setPresetStyle = () => {
  presetStyle.value = presetStyles.find(item => {
    if (item.style.fontSize === selectedWidget.value?.style?.fontSize &&
        item.style.fontWeight === selectedWidget.value?.style?.fontWeight) {
      return item.value
    }
  })
}

const applyPresetStyle = (newVal: any) => {
  if (newVal) {
    Object.assign(selectedWidget.value.style, newVal.style);
    emitter.emit(EmitterEvents.RICH_TEXT_COMMAND, { action: { command: 'fontsize', value: newVal.style.fontSize } })
  }
};

watch(selectedWidget, () => {
    emitter.emit(EmitterEvents.RICH_TEXT_COMMAND, { action: { command: 'getFontSize' } })
})

watch(presetStyle, applyPresetStyle);

const defaultFontSize = computed({
  get:() => {
    if (selectedWidget.value && selectedWidget.value.type === 'table') {
      if (selectedCells.value && selectedCells.value.length > 0) {
        const rowIndex = selectedCells.value[0].split('_')[0]
        const colIndex = selectedCells.value[0].split('_')[1]
        if (selectedWidget.value.data && selectedWidget.value.data[rowIndex][colIndex]) {
          const selectedCell = selectedWidget.value.data[rowIndex][colIndex]
          if (!selectedCell.style) {
            selectedCell.style = {}
          }
          return selectedCell.style.fontSize ? parseInt(selectedCell.style.fontSize) : 16
        }
      }
    }

    if (textSelected.value || (selectedWidget.value?.type === 'reg_text')) return selectionFontSize.value
    return (selectedWidget.value?.style?.fontSize ? parseInt(selectedWidget.value?.style?.fontSize) : 16)
  },
  set(val)
  {
    setFontSize(val)
  }
})

const updateFontSize = (updateEvent) => {

  setFontSize(updateEvent.value)
}


const backgroundColorHEX = computed({
  get: () => selectedWidget.value?.style?.backgroundColor ?? 'transparent',
  set: val => selectedWidget.value && (selectedWidget.value.style.backgroundColor = val)
})

const colorHEX = computed({
  get() {
    return textSelected.value || (selectedWidget.value?.type === 'reg_text') ? selectionColor.value : selectedWidget.value?.style?.color ?? '#000'
  },
  set(val) {
    setTextStyle('color', val, 'color')
  }
})

const setTextStyle = (property: string, value: string, command: string) => {
  if (selectedWidget.value && selectedWidget.value.type === 'table') {
    if (selectedCells.value) {
      for (let i = 0; i < selectedCells.value.length; i++) {
        const rowIndex = selectedCells.value[i].split('_')[0]
        const colIndex = selectedCells.value[i].split('_')[1]

        if (selectedWidget.value.data && selectedWidget.value.data[rowIndex][colIndex]) {
          const selectedCell = selectedWidget.value.data[rowIndex][colIndex]
          if (!selectedCell.style) {
            selectedCell.style = {}
          }
          selectedCell.style[property] = value
        }
      }
    }
    return
  }

  if (textSelected.value  || (selectedWidget.value && selectedWidget.value.type === 'reg_text')) {
      emitter.emit(EmitterEvents.RICH_TEXT_COMMAND, { action: { command: command, value } })
  }
  else if (selectedWidget.value) {
    selectedWidget.value.style[property] = value;
  }
};

const addLink = () => {
  emitter.emit(EmitterEvents.RICH_TEXT_COMMAND, { action: { command: 'link', value: link.value } })
  linkVisible.value.hide()
}

const getCurrentTextStyle = (property) => {
  if (selectedWidget.value && selectedWidget.value.type === 'table') {
    if (selectedCells.value && selectedCells.value.length > 0) {
      const rowIndex = selectedCells.value[0].split('_')[0]
      const colIndex = selectedCells.value[0].split('_')[1]
      if (selectedWidget.value.data && selectedWidget.value.data[rowIndex][colIndex]) {
        const selectedCell = selectedWidget.value.data[rowIndex][colIndex]
        if (!selectedCell.style) {
          selectedCell.style = {}
        }
        return selectedCell.style[property]
      }
    }
  }
  return selectedWidget.value?.style[property]
}

const setBoldText = () => setTextStyle('fontWeight', isBold.value ? 'normal' : 'bold', 'bold')
const setItalicText = () => setTextStyle('fontStyle', isItalic.value ? 'normal' : 'italic', 'em')
const setUnderlineText = () => setTextStyle('textDecoration', isUnderlined.value ? 'none' : 'underline', 'underline')

const op = ref();
const bulletListPanelVisible = ref(false)
const emitRichTextCommand = (command: string, value?: string) => {
  emitter.emit(EmitterEvents.RICH_TEXT_COMMAND, { action: { command, value } })
}
const bulletListStyleTypeOption = ref(['disc', 'circle', 'square'])
const orderedListStyleTypeOption = ref(['decimal', 'lower-roman', 'upper-roman', 'lower-alpha', 'upper-alpha', 'lower-greek'])

const setFontSize = (fontSize: number) => {
  if (selectedWidget.value && selectedWidget.value.type === 'table') {
    if (selectedCells.value) {
      for (let i = 0; i < selectedCells.value.length; i++) {
        const rowIndex = selectedCells.value[i].split('_')[0]
        const colIndex = selectedCells.value[i].split('_')[1]

        if (selectedWidget.value.data && selectedWidget.value.data[rowIndex][colIndex]) {
          const selectedCell = selectedWidget.value.data[rowIndex][colIndex]
          if (!selectedCell.style) {
            selectedCell.style = {}
          }
          selectedCell.style.fontSize = `${fontSize}px`
        }
      }
      return
    }
  }

  if (textSelected.value || (selectedWidget.value && selectedWidget.value.type === 'reg_text')) {
    emitter.emit(EmitterEvents.RICH_TEXT_COMMAND, { action: { command: 'fontsize', value: `${fontSize}px` } })
  }
  else if (selectedWidget.value) {
    selectedWidget.value.style.fontSize = `${fontSize}px`;
  }

  selectionFontSize.value = fontSize

};

watchEffect(() => {
  if (selectedWidget.value) setPresetStyle()
})

</script>

<style lang="scss" scoped>

.font-incrementer {
  width: 200px;
}

.font-incrementer > .p-inputtext {
  text-align: center;
  width: 50px;
}


.font-incrementer > .p-button {
  border: none;
}

.settings-list {
  @include flex-grid-layout();
}

.p-dropdown.p-component.p-inputwrapper.p-inputwrapper-filled.preset-dropdown,
.p-dropdown.p-component.p-inputwrapper.preset-dropdown
{
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preset-dropdown > .p-dropdown-label {
  font-size: 12px
}

.preset-dropdown > .p-dropdown-trigger {
  font-size: 12px;
}

.preset-dropdown.handler-item {
  width: 100px;
}

.handler-item {
  width: 32px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 4px 2px;
  border-radius: $borderRadius;

  &:not(.group-btn):hover {
    background-color: #f9f9f9;
  }

  &.active {
    border-color: $themeColor;
    background-color: #e0e0e0;
    color: $themeColor;
  }

  &.group-btn {
    width: auto;
    margin-right: 4px;

    &:hover {
      background-color: #f3f3f3;
    }

    .icon, .arrow {
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .icon {
      width: 26px;
      padding: 0 2px;

      &:hover {
        background-color: #e9e9e9;
      }

      &.active {
        color: $themeColor;
      }
    }

    .arrow {
      font-size: 12px;

      &:hover {
        background-color: #e9e9e9;
      }
    }
  }
}

:deep(.p-divider.p-divider-vertical) {
  margin: 0 0.5rem;
  padding: 0.5rem 0;
}
</style>
